import { useQuery } from '@apollo/client';
import { MovieCreation } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMoveShow } from '~/api/shows';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { BulkActions, ShowGrid, ShowMoveDocument, Table } from '../list/components/';
import { FilterAccordion } from '../list/components/FilterAccordion';
import { FilterChips } from '../list/components/FilterChips';
import { BulkTagDialog } from '../list/components/bulk-tag-dialog';
import { ShowFiltersProvider, useShowFiltersParams, useShowFiltersReducer } from '../list/context';
import type { ShowList__Show as Show } from '../queries/queries.generated';
import { ShowSearchDocument } from '../queries/search.generated';

export const ShowSearch = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const backToFolder = searchParams.get('backToFolder')?.trim() ?? '';
  const view = searchParams.get('view')?.trim() || 'List';

  const link = useLink();
  const navigate = useNavigate();

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [tagShowDialogOpen, setTagShowDialogOpen] = useState(false);
  const [moveShowDialogOpen, setMoveShowDialogOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState<Show>();
  const [moveShow, moveShowInfo] = useMoveShow();

  const filterParams = useShowFiltersParams();
  const tags = filterParams.tags;

  const { data, loading } = useQuery(ShowSearchDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, tags },
    skip: !search,
    onCompleted: (data) => handleOutOfBounds(data.network?.shows.pageInfo),
  });

  const { data: showMoveData } = useQuery(ShowMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveShowDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      showMoveData?.network?.showFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ?? [],
    [showMoveData],
  );

  const totalCount = useTotalCount(data?.network?.shows.pageInfo.nodesCount);
  const shows = useMemo(() => data?.network?.shows.nodes ?? [], [data]);

  const showTags = useMemo(
    () => data?.network?.showTags.map((x) => x.name),
    [data?.network?.showTags],
  );
  const filterCounts = data?.network?.showFilterInfo;

  const [showFiltersState, showFiltersDispatch] = useShowFiltersReducer(filterParams, showTags);

  const filtersActive = useMemo(() => tags.length > 0, [tags]);

  const canDestroySelectedShows = selectedIds.every((selectedId) => {
    const show = shows.find((show) => show.id === selectedId);
    return show?.canDestroy.value;
  });

  const handleClearFilter = useCallback(() => {
    const folderId = backToFolder || folderTree.id;
    navigate(link(`/shows/${folderId}`));
  }, [backToFolder, folderTree.id, link, navigate]);

  const handleSearch = useCallback(
    (newSearch: string) => {
      if (newSearch === '') {
        handleClearFilter();
        return;
      }
      setSearchParams((params) => {
        params.set('search', newSearch);
        params.set('backToFolder', backToFolder);
        return params;
      });
    },
    [backToFolder, handleClearFilter, setSearchParams],
  );

  return (
    <ShowFiltersProvider
      {...showFiltersState}
      clearFilters={() => showFiltersDispatch({ type: 'CLEAR_FILTERS' })}
      reinitialize={(params) => showFiltersDispatch({ type: 'REINITIALIZE', params })}
      setFilter={(value) => showFiltersDispatch({ type: 'SET_FILTER', ...value })}
      toggleFilter={(field) => showFiltersDispatch({ type: 'TOGGLE_FILTER', field })}
      togglePanel={() => showFiltersDispatch({ type: 'TOGGLE_PANEL' })}
    >
      <Helmet title={t('pages.shows.search.title')} />
      <Toolbar titleIcon={<MovieCreation />} titleText={t('pages.shows.search.title')} />

      <ListPageContainer>
        <FilterSidebar open={showFiltersState.panel.open} persistAs="showSearch:filterWidth">
          <FilterAccordion filterCounts={filterCounts} />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => showFiltersDispatch({ type: 'TOGGLE_PANEL' })}
                />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.shows.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 && (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagShow={() => setTagShowDialogOpen(true)}
                    onMoveShow={() => setMoveShowDialogOpen(true)}
                    canDestroySelectedShows={canDestroySelectedShows}
                  />
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="common.pluralize.show_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <Table
                    loading={loading}
                    shows={shows}
                    folders={[]}
                    selectedShow={selectedShow}
                    onCheck={setSelectedIds}
                    onSelectedShow={setSelectedShow}
                    selectedIds={selectedIds}
                    search
                  />
                ) : (
                  <ShowGrid
                    shows={shows}
                    folders={[]}
                    selectedShow={selectedShow}
                    onCheck={setSelectedIds}
                    onSelectedShow={setSelectedShow}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await moveShow({
                refetchQueries: ['ShowSearch'],
                variables: {
                  input: {
                    showFolderId: targetFolderId,
                    showIds: selectedIds as number[],
                  },
                },
              });
              setSelectedIds([]);
            }}
            loading={moveShowInfo.loading}
            onClose={() => setMoveShowDialogOpen(false)}
            open={moveShowDialogOpen}
            i18nKey="pages.shows.list.dialogs.move_show.title"
          />

          {tagShowDialogOpen && (
            <BulkTagDialog
              showIds={selectedIds as string[]}
              showTags={data?.network?.showTags.map((x) => x.name) || []}
              onClose={() => setTagShowDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </ShowFiltersProvider>
  );
};
